/**
 * TA Audit · Data Ops · Feb 2026 (v2 — AI suggester)
 *
 * Lists every asset whose therapeutic_area + indication both fail to
 * resolve to a canonical TA group. Assets here are at risk of shipping
 * thin AI Mapping results (Path A + Path C + Path E all go silent).
 *
 * v2 (Feb 2026): "Suggest with AI" buttons — per-asset and per-group —
 * run Claude over each asset's description and propose a canonical TA
 * inline, with an Apply button to persist.
 *
 * Route: /data-ops/ta-audit
 * Endpoints:
 *   GET  /api/data-ops/ta-audit
 *   POST /api/data-ops/ta-audit/suggest {asset_ids: [...]}
 *   POST /api/data-ops/ta-audit/apply   {asset_id, therapeutic_area}
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, ExternalLink, Search, Sparkles, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { TaBulkRunner } from '@/components/data-ops/TaBulkRunner';

const API = process.env.REACT_APP_BACKEND_URL;

const CONF_TONE = {
  high: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function TaAuditPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  // asset_id -> {loading, proposal, applying}
  const [proposals, setProposals] = useState({});

  const fetchAudit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/data-ops/ta-audit`, { withCredentials: true });
      setData(res.data);
      // Hydrate stored (not yet applied) proposals from bulk runs.
      const stored = res.data?.proposals || {};
      setProposals((prev) => {
        const next = { ...prev };
        Object.entries(stored).forEach(([id, p]) => { next[id] = { loading: false, proposal: p, applying: false }; });
        return next;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to load audit';
      setError(String(msg));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); }, []);

  const suggestBatch = async (assetIds) => {
    if (!assetIds || assetIds.length === 0) return;
    // Mark them loading
    setProposals((prev) => {
      const next = { ...prev };
      assetIds.forEach((id) => { next[id] = { ...(next[id] || {}), loading: true }; });
      return next;
    });
    try {
      const res = await axios.post(
        `${API}/api/data-ops/ta-audit/suggest`,
        { asset_ids: assetIds },
        { withCredentials: true },
      );
      const proposalsById = {};
      for (const p of (res.data?.proposals || [])) {
        proposalsById[p.asset_id] = p;
      }
      setProposals((prev) => {
        const next = { ...prev };
        assetIds.forEach((id) => {
          const p = proposalsById[id];
          next[id] = { loading: false, proposal: p || null, applying: false };
        });
        return next;
      });
      const successCount = Object.values(proposalsById).filter((p) => p.therapeutic_area).length;
      toast.success(
        `Suggested TAs for ${successCount} of ${assetIds.length} asset${assetIds.length === 1 ? '' : 's'}`
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'AI suggestion failed';
      toast.error(msg);
      setProposals((prev) => {
        const next = { ...prev };
        assetIds.forEach((id) => { next[id] = { loading: false, proposal: null, applying: false }; });
        return next;
      });
    }
  };

  const applyProposal = async (assetId, therapeuticArea) => {
    setProposals((prev) => ({ ...prev, [assetId]: { ...(prev[assetId] || {}), applying: true } }));
    try {
      await axios.post(
        `${API}/api/data-ops/ta-audit/apply`,
        { asset_id: assetId, therapeutic_area: therapeuticArea },
        { withCredentials: true },
      );
      toast.success(`Applied TA "${therapeuticArea}" — refreshing`);
      // Optimistically drop this asset from the visible list so the user
      // sees immediate feedback; then re-fetch to update counts.
      setData((prev) => {
        if (!prev) return prev;
        const groups = (prev.groups || [])
          .map((g) => ({ ...g, assets: (g.assets || []).filter((a) => a.asset_id !== assetId) }))
          .map((g) => ({ ...g, count: g.assets.length }))
          .filter((g) => g.count > 0);
        return {
          ...prev,
          groups,
          unresolved_count: Math.max(0, (prev.unresolved_count || 0) - 1),
        };
      });
      setProposals((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Apply failed';
      toast.error(msg);
      setProposals((prev) => ({ ...prev, [assetId]: { ...(prev[assetId] || {}), applying: false } }));
    }
  };

  const dismissProposal = (assetId) => {
    setProposals((prev) => {
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
  };

  // Bulk runner (background job with progress). Group "Suggest all" uses
  // it too so groups >25 assets aren't silently truncated by /suggest.
  const bulkStartRef = useRef(null);
  const pendingConfidences = Object.values(proposals).filter((p) => p.proposal?.therapeutic_area).map((p) => p.proposal.confidence);

  const groups = (data?.groups || []).filter((g) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (g.therapeutic_area || '').toLowerCase().includes(q) ||
      (g.indication || '').toLowerCase().includes(q) ||
      (g.assets || []).some(
        (a) => (a.asset_name || '').toLowerCase().includes(q) ||
               (a.company_name || '').toLowerCase().includes(q)
      )
    );
  });

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="ta-audit-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-amber-600" />
            TA Audit — assets with unrecognised therapeutic areas
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Every asset below has both a <code className="text-xs bg-slate-100 px-1 rounded">therapeutic_area</code> and{' '}
            <code className="text-xs bg-slate-100 px-1 rounded">indication</code> that don&apos;t match any canonical TA group
            in our taxonomy. When you run AI Mapping on these assets, Paths A + C + E go silent and you&apos;ll get
            a thin, non-deterministic result (6-20 pharmas instead of 60-100+). Click{' '}
            <span className="font-medium text-violet-700">Suggest with AI</span> to let Claude pick a canonical TA
            based on the asset&apos;s indication + description, then Apply to persist.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAudit} disabled={loading} data-testid="ta-audit-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total assets</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid="ta-audit-total">{data.total_assets}</div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs uppercase tracking-wide text-amber-700">Unresolved assets</div>
            <div className="text-2xl font-semibold text-amber-900 mt-1" data-testid="ta-audit-unresolved">{data.unresolved_count}</div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Resolved (OK)</div>
            <div className="text-2xl font-semibold text-emerald-900 mt-1" data-testid="ta-audit-resolved">
              {data.total_assets - data.unresolved_count}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-800 text-sm">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Scanning your asset library…
        </div>
      )}

      {data && data.unresolved_count === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-emerald-800">
          🎉 Every asset in your library has a canonical TA. AI Mapping will fire on full power for all of them.
        </div>
      )}

      {data && data.unresolved_count > 0 && (
        <>
          <TaBulkRunner
            unresolvedCount={data.unresolved_count}
            pendingConfidences={pendingConfidences}
            onFinished={fetchAudit}
            registerStart={(fn) => { bulkStartRef.current = fn; }}
          />
          <p className="text-xs text-slate-500 -mt-3" data-testid="ta-audit-groups-note">
            Unresolved assets are grouped by their raw TA / indication text below — the group counts add up to {data.unresolved_count}.
            &quot;Suggest all&quot; on a group runs only that group; the button above runs every group.
          </p>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by TA, indication, asset or company name…"
              className="pl-9"
              data-testid="ta-audit-filter"
            />
          </div>

          <div className="space-y-3">
            {groups.map((g) => {
              const groupAssetIds = (g.assets || []).map((a) => a.asset_id);
              const anyGroupLoading = groupAssetIds.some((id) => proposals[id]?.loading);
              return (
                <div
                  key={g.key}
                  className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                  data-testid={`ta-audit-group-${g.key}`}
                >
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-900">TA:</span>{' '}
                      <code className="text-xs bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                        {g.therapeutic_area || '(empty)'}
                      </code>{' '}
                      <span className="text-slate-400 mx-1">·</span>{' '}
                      <span className="font-semibold text-slate-900">Indication:</span>{' '}
                      <code className="text-xs bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                        {g.indication || '(empty)'}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                        disabled={anyGroupLoading || groupAssetIds.length === 0}
                        onClick={() => (groupAssetIds.length > 25 && bulkStartRef.current)
                          ? bulkStartRef.current(groupAssetIds)
                          : suggestBatch(groupAssetIds)}
                        data-testid={`ta-audit-suggest-group-${g.key}`}
                      >
                        {anyGroupLoading ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Suggesting…</>
                        ) : (
                          <><Sparkles className="w-3 h-3 mr-1" /> Suggest all ({g.count})</>
                        )}
                      </Button>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                        {g.count} asset{g.count === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(g.assets || []).map((a) => {
                      const state = proposals[a.asset_id] || {};
                      return (
                        <div key={a.asset_id} className="px-4 py-2 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-900 truncate">{a.asset_name}</div>
                              <div className="text-xs text-slate-500 truncate">
                                {a.company_name || '(no biotech linked)'}
                                {a.stage ? ` · ${a.stage}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {!state.proposal && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                                  disabled={state.loading}
                                  onClick={() => suggestBatch([a.asset_id])}
                                  data-testid={`ta-audit-suggest-${a.asset_id}`}
                                >
                                  {state.loading ? (
                                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> …</>
                                  ) : (
                                    <><Sparkles className="w-3 h-3 mr-1" /> Suggest with AI</>
                                  )}
                                </Button>
                              )}
                              {a.company_id && (
                                <a
                                  href={`/biotech-companies/${a.company_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                  data-testid={`ta-audit-open-${a.asset_id}`}
                                >
                                  Open <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>

                          {state.proposal && (
                            <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/60 p-3 space-y-2" data-testid={`ta-audit-proposal-${a.asset_id}`}>
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                                  <span className="font-semibold text-violet-900">AI suggests:</span>
                                  {state.proposal.therapeutic_area ? (
                                    <code className="text-xs bg-white border border-violet-300 px-1.5 py-0.5 rounded font-semibold text-violet-900 capitalize">
                                      {state.proposal.therapeutic_area}
                                    </code>
                                  ) : (
                                    <span className="text-xs italic text-slate-500">No confident pick</span>
                                  )}
                                  <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${CONF_TONE[state.proposal.confidence] || CONF_TONE.medium}`}>
                                    {state.proposal.confidence}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {state.proposal.therapeutic_area && (
                                    <Button
                                      size="sm"
                                      className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                      disabled={state.applying}
                                      onClick={() => applyProposal(a.asset_id, state.proposal.therapeutic_area)}
                                      data-testid={`ta-audit-apply-${a.asset_id}`}
                                    >
                                      {state.applying ? (
                                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying…</>
                                      ) : (
                                        <><Check className="w-3 h-3 mr-1" /> Apply</>
                                      )}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs text-slate-500 hover:text-slate-800"
                                    onClick={() => dismissProposal(a.asset_id)}
                                    data-testid={`ta-audit-dismiss-${a.asset_id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              {state.proposal.reasoning && (
                                <p className="text-xs text-slate-700 leading-relaxed">{state.proposal.reasoning}</p>
                              )}
                              {state.proposal.alternates && state.proposal.alternates.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap text-xs text-slate-500">
                                  <span>Alternates:</span>
                                  {state.proposal.alternates.map((alt) => (
                                    <button
                                      key={alt}
                                      onClick={() => applyProposal(a.asset_id, alt)}
                                      disabled={state.applying}
                                      className="capitalize px-1.5 py-0.5 rounded border border-slate-300 bg-white hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-800"
                                      data-testid={`ta-audit-apply-alt-${a.asset_id}-${alt}`}
                                    >
                                      {alt}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {groups.length === 0 && query.trim() && (
              <div className="text-center py-8 text-slate-400 text-sm">No matches for &quot;{query}&quot;</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
